<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref } from "vue"
import { useI18n } from "vue-i18n"
import type { ConsentRequest } from "@types"
import type { Address } from "@types"
import type { CompanyType, OwnerOutput } from "@core/composables"
import type { MultiConsentBuildingRecap } from "@core/types/source/multi_consent"
import type { Services } from "@services"
import { SOURCES_TYPES } from "@core/constants"
import { formatAddress, formatIdSource } from "@core/helpers"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            ownerTitle: "Titulaire",
            ownerInfo: "Informations du titulaire",
            ownerCompanyTypeEntreprise: "Entreprise",
            ownerCompanyTypeAsso: "Association",
            ownerCompanyTypeCollectivite: "Collectivité",
            ownerCompanyTypeCopro: "Copropriété",
            ownerUser: "Représentant / Client",
            buildingsTitleWithCount:
                "{buildings} bâtiment(s) • {meters} compteur(s)",
            submit: "Autoriser l'accès aux données",
            report: "Signaler un problème",
            invalid_title: "Certains compteurs ne peuvent pas être signés",
            invalid_message_one:
                "1 compteur possède déjà un consentement signé par un autre utilisateur sur l'application. Si vous voulez avoir accès à ce compteur veuillez faire la demande auprès des personnes possédant les droits sur ces compteurs.",
            invalid_message_many:
                "{count} compteurs possèdent déjà des consentements signés par d'autres utilisateurs sur l'application. Si vous voulez avoir accès à ces compteurs veuillez faire la demande auprès des personnes possédant les droits sur ces compteurs.",
            invalid_list: "Liste des compteurs n'ayant pas pu être signés :",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type ConsentType = "pdl" | "pce" | "both"

/**
 * Clés i18n par type d'organisation (aligné sur `CompanyType`)
 */
const OWNER_COMPANY_TYPE_LABEL_KEYS = {
    entreprise: "ownerCompanyTypeEntreprise",
    asso: "ownerCompanyTypeAsso",
    collectivite: "ownerCompanyTypeCollectivite",
    copro: "ownerCompanyTypeCopro",
} as const satisfies Record<CompanyType, string>

interface Props {
    /** Identifiant de la demande multi */
    requestId: string
    /** Titulaire (entreprise + représentant) */
    owner: OwnerOutput
    /** Liste des bâtiments avec leurs compteurs */
    buildings?: MultiConsentBuildingRecap[]
    /** Type de consentement (PDL, PCE ou les deux) */
    type?: ConsentType
    /** Durée du consentement en mois */
    duration?: number
    /** Demandes invalides (compteurs déjà signés par un autre) */
    invalidConsentRequests?: ConsentRequest[]
}

interface Emits {
    (e: "done:sign"): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    buildings: () => [],
    type: "pdl",
    duration: 60,
    invalidConsentRequests: () => [],
})

const emit = defineEmits<Emits>()

// ================================
// SERVICES
// ================================
const services = inject<Services>("services")!

// ================================
// STATE
// ================================
/** True quand toutes les conditions de consentement sont acceptées (SourceConsentSignConditions) */
const consentConditionsAccepted = ref(false)
/** Collapsible récap titulaire (aligné sur SourceMultiAppendFormSummary) */
const isOwnerOpen = ref(false)
/** Collapse bâtiments ouverte ou fermée */
const isBuildingsOpen = ref(false)
/** Chargement en cours lors du submit */
const loading = ref(false)
/** Message d'erreur API */
const error = ref("")
/** Affichage de la modal support (Signaler un problème) */
const supportModalVisible = ref(false)

// ================================
// COMPUTED
// ================================
/** Nombre total de compteurs (sources) */
const getSourcesLength = computed(() => {
    return props.buildings.reduce((acc, b) => acc + (b.sources?.length ?? 0), 0)
})

/**
 * Adresse formatée de l'entreprise
 */
const getCompanyAddress = computed(() => {
    return props.owner.company?.address
        ? formatAddress(props.owner.company.address)
        : ""
})

/**
 * Libellé du type d'organisation (Entreprise, Association, Collectivité, Copropriété)
 */
const getOwnerCompanyTypeLabel = computed(() => {
    const type = props.owner.company?.type
    if (!type) return ""
    const key = OWNER_COMPANY_TYPE_LABEL_KEYS[type as CompanyType]
    return key ? t(key) : type
})

/** Message invalides (singulier / pluriel) */
const getInvalidMessage = computed(() =>
    (props.invalidConsentRequests?.length ?? 0) <= 1
        ? t("invalid_message_one")
        : t("invalid_message_many", {
              count: props.invalidConsentRequests?.length ?? 0,
          })
)

/** Liste des compteurs invalides formatés pour affichage */
const getInvalidMetersFormatted = computed(() =>
    (props.invalidConsentRequests ?? [])
        .map((req) =>
            formatIdSource(
                req.source?.idsource ?? "",
                req.source?.idtype ?? "pdl"
            )
        )
        .filter(Boolean)
        .join(" • ")
)

// ================================
// INTERACTIONS
// ================================
/**
 * Soumission du formulaire : appelle l'API update state puis émet done:sign.
 */
async function onSubmit(): Promise<void> {
    if (!consentConditionsAccepted.value || loading.value) return
    try {
        error.value = ""
        loading.value = true
        await services.$updateConsentRequestMultiById(props.requestId)
        emit("done:sign")
    } catch (err: unknown) {
        error.value = err instanceof Error ? err.message : String(err)
    } finally {
        loading.value = false
    }
}

/**
 * Ouvre la modal d'aide / signalement (même flux que le bouton support applicatif).
 */
function onReportProblem(): void {
    supportModalVisible.value = true
}

/**
 * Gère le clic sur le header du collapsible titulaire
 */
function onClickOwnerHeader(): void {
    isOwnerOpen.value = !isOwnerOpen.value
}

/**
 * Gère le clic sur le header du collapsible bâtiments (aligné SourceMultiAppendFormSummary).
 */
function onClickBuildingsHeader(): void {
    isBuildingsOpen.value = !isBuildingsOpen.value
}
</script>

<template>
    <div class="source-form-multi-sign-invite flex flex-col gap-6 w-full">
        <!-- ================================ -->
        <!-- COLLAPSIBLE TITULAIRE (aligné SourceMultiAppendFormSummary) -->
        <!-- ================================ -->
        <div
            class="source-form-multi-sign-invite__owner flex flex-col border-b border-gray-200 pb-6 w-full"
        >
            <div
                class="flex items-center justify-between w-full cursor-pointer py-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                role="button"
                tabindex="0"
                :aria-expanded="isOwnerOpen"
                aria-controls="source-form-multi-sign-invite-owner-panel"
                @click="onClickOwnerHeader"
                @keydown.enter.prevent="onClickOwnerHeader"
                @keydown.space.prevent="onClickOwnerHeader"
            >
                <h3 class="text-base font-semibold text-gray-700">
                    {{ t("ownerTitle") }}
                </h3>
                <BaseIcon
                    :icon="
                        isOwnerOpen ? 'pi pi-chevron-up' : 'pi pi-chevron-down'
                    "
                    class="text-gray-500"
                />
            </div>

            <div
                v-show="isOwnerOpen"
                id="source-form-multi-sign-invite-owner-panel"
                class="flex flex-col gap-4 pt-4"
            >
                <p class="text-sm text-gray-500">{{ t("ownerInfo") }}</p>

                <div
                    v-if="owner.company"
                    class="flex flex-col gap-1 p-3 bg-gray-50 rounded border border-gray-200"
                >
                    <span class="text-xs font-medium text-gray-500">
                        {{ getOwnerCompanyTypeLabel }}
                    </span>
                    <p class="text-sm text-gray-700">
                        {{ owner.company.name }}
                        <template v-if="getCompanyAddress">
                            • {{ getCompanyAddress }}
                        </template>
                        <template
                            v-if="
                                owner.company.type !== 'collectivite' &&
                                owner.company.idcompany
                            "
                        >
                            • {{ owner.company.idcompany }}
                        </template>
                    </p>
                </div>

                <div
                    class="flex flex-col gap-1 p-3 bg-gray-50 rounded border border-gray-200"
                >
                    <span
                        v-if="owner.company"
                        class="text-xs font-medium text-gray-500"
                    >
                        {{ t("ownerUser") }}
                    </span>
                    <p class="text-sm text-gray-700">
                        {{ owner.user.firstname }} {{ owner.user.lastname }} •
                        {{ owner.user.username?.toLowerCase() }}
                    </p>
                </div>
            </div>
        </div>

        <!-- ================================ -->
        <!-- COLLAPSIBLE BÂTIMENTS (aligné SourceMultiAppendFormSummary) -->
        <!-- ================================ -->
        <div
            class="source-form-multi-sign-invite__buildings flex flex-col pb-6 w-full"
        >
            <div
                class="flex items-center justify-between w-full cursor-pointer py-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                role="button"
                tabindex="0"
                :aria-expanded="isBuildingsOpen"
                aria-controls="source-form-multi-sign-invite-buildings-panel"
                @click="onClickBuildingsHeader"
                @keydown.enter.prevent="onClickBuildingsHeader"
                @keydown.space.prevent="onClickBuildingsHeader"
            >
                <h3 class="text-base font-semibold text-gray-700">
                    {{
                        t("buildingsTitleWithCount", {
                            buildings: buildings.length,
                            meters: getSourcesLength,
                        })
                    }}
                </h3>
                <BaseIcon
                    :icon="
                        isBuildingsOpen
                            ? 'pi pi-chevron-up'
                            : 'pi pi-chevron-down'
                    "
                    class="text-gray-500"
                />
            </div>

            <div
                v-show="isBuildingsOpen"
                id="source-form-multi-sign-invite-buildings-panel"
                class="flex flex-col gap-4 pt-4"
            >
                <div
                    v-for="(building, index) in buildings"
                    :key="`building-${index}`"
                    class="p-4 border border-gray-200 rounded bg-gray-50 hover:border-cyan-400 transition-colors"
                >
                    <ItemBuildingRecap
                        :name="building.name"
                        :address="(building.address ?? null) as Address"
                        :pdl="
                            building.sources
                                .filter((s) => s.idtype === SOURCES_TYPES.PDL)
                                .map((s) => s.idsource)
                        "
                        :pce="
                            building.sources
                                .filter((s) => s.idtype === SOURCES_TYPES.PCE)
                                .map((s) => s.idsource)
                        "
                    />
                </div>
            </div>
        </div>

        <!-- ================================ -->
        <!-- ALERTE COMPTEURS INVALIDES -->
        <!-- ================================ -->
        <BaseMessage
            v-if="invalidConsentRequests && invalidConsentRequests.length > 0"
            severity="warn"
            :closable="false"
            class="source-form-multi-sign-invite__invalid w-full"
        >
            <p class="font-medium">{{ t("invalid_title") }}</p>
            <p>{{ getInvalidMessage }}</p>
            <p>{{ t("invalid_list") }}</p>
            <p class="text-sm mt-1">{{ getInvalidMetersFormatted }}</p>
        </BaseMessage>

        <!-- ================================ -->
        <!-- CONDITIONS DE CONSENTEMENT -->
        <!-- ================================ -->
        <SourceConsentSignConditions
            v-model="consentConditionsAccepted"
            class="w-full"
            :type="type"
            :duration="duration"
        />

        <!-- ================================ -->
        <!-- ACTIONS -->
        <!-- ================================ -->
        <BaseButton
            :label="t('submit')"
            :loading="loading"
            :disabled="!consentConditionsAccepted || loading"
            size="small"
            @click="onSubmit"
        />
        <button
            type="button"
            class="text-sm text-primary hover:underline"
            @click="onReportProblem"
        >
            {{ t("report") }}
        </button>

        <BaseMessage
            v-if="error"
            severity="error"
            :closable="false"
            class="w-full"
        >
            {{ error }}
        </BaseMessage>

        <SupportModal
            v-model:visible="supportModalVisible"
            modal-id="source-form-multi-sign-invite-support"
        />
    </div>
</template>
